import * as pdfjsLib from 'pdfjs-dist'

// Configure PDF.js worker - use CDN to match installed pdfjs-dist version
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`

/**
 * Sanitize text for PostgreSQL by removing NULL bytes and control characters
 * PostgreSQL text fields cannot contain NULL bytes (0x00)
 */
function sanitizeText(text: string): string {
  // Remove NULL bytes and other problematic control characters
  // Keep newlines (\n), carriage returns (\r), and tabs (\t)
  return text.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')
}

export interface ExtractedPage {
  pageNumber: number
  text: string
}

export interface PageImage {
  pageNumber: number
  dataUrl: string  // base64 PNG data URL
  width: number
  height: number
}

export interface ExtractedPdf {
  fileName: string
  totalPages: number
  pages: ExtractedPage[]
  fullText: string
}

/**
 * Progress callback for PDF operations
 */
export interface PdfProgressCallback {
  (progress: {
    stage: 'loading' | 'extracting' | 'rendering' | 'complete'
    currentPage: number
    totalPages: number
    percentage: number
    message: string
  }): void
}

/**
 * Render PDF pages to images for visual analysis
 * @param pdfData - PDF as ArrayBuffer or base64 string
 * @param options - Rendering options
 * @returns Array of page images as base64 PNG
 */
export async function renderPdfToImages(
  pdfData: ArrayBuffer | string,
  options: {
    scale?: number      // Default 1.5 for good quality/size balance
    maxPages?: number   // Limit pages to render (default all)
    pageNumbers?: number[]  // Specific pages to render
    onProgress?: PdfProgressCallback
  } = {}
): Promise<PageImage[]> {
  const { scale = 1.5, maxPages, pageNumbers, onProgress } = options

  // Convert base64 to ArrayBuffer if needed
  let data: ArrayBuffer
  if (typeof pdfData === 'string') {
    const binary = atob(pdfData)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }
    data = bytes.buffer
  } else {
    data = pdfData
  }

  onProgress?.({
    stage: 'loading',
    currentPage: 0,
    totalPages: 0,
    percentage: 0,
    message: 'Loading PDF document...',
  })

  const pdf = await pdfjsLib.getDocument({ data }).promise
  const images: PageImage[] = []

  // Determine which pages to render
  let pagesToRender: number[]
  if (pageNumbers) {
    pagesToRender = pageNumbers.filter(p => p >= 1 && p <= pdf.numPages)
  } else {
    const totalPages = maxPages ? Math.min(maxPages, pdf.numPages) : pdf.numPages
    pagesToRender = Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const totalToRender = pagesToRender.length

  for (let i = 0; i < pagesToRender.length; i++) {
    const pageNum = pagesToRender[i]
    const percentage = Math.round(((i + 1) / totalToRender) * 100)

    onProgress?.({
      stage: 'rendering',
      currentPage: pageNum,
      totalPages: totalToRender,
      percentage,
      message: `Rendering page ${pageNum} of ${totalToRender}...`,
    })

    const page = await pdf.getPage(pageNum)
    const viewport = page.getViewport({ scale })

    // Create canvas
    const canvas = document.createElement('canvas')
    canvas.width = viewport.width
    canvas.height = viewport.height

    const context = canvas.getContext('2d')
    if (!context) {
      console.warn(`[renderPdfToImages] Failed to get canvas context for page ${pageNum}`)
      continue
    }

    // Render page to canvas
    await page.render({
      canvasContext: context,
      viewport,
    }).promise

    // Convert to base64 PNG
    const dataUrl = canvas.toDataURL('image/png')

    images.push({
      pageNumber: pageNum,
      dataUrl,
      width: viewport.width,
      height: viewport.height,
    })
  }

  onProgress?.({
    stage: 'complete',
    currentPage: totalToRender,
    totalPages: totalToRender,
    percentage: 100,
    message: `Rendered ${totalToRender} pages`,
  })

  return images
}

/**
 * Extract text from PDF with progress tracking
 */
export async function extractTextFromPdf(
  file: File,
  onProgress?: PdfProgressCallback
): Promise<ExtractedPdf> {
  onProgress?.({
    stage: 'loading',
    currentPage: 0,
    totalPages: 0,
    percentage: 0,
    message: 'Loading PDF...',
  })

  const arrayBuffer = await file.arrayBuffer()
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise

  const totalPages = pdf.numPages
  const pages: ExtractedPage[] = []

  for (let i = 1; i <= totalPages; i++) {
    const percentage = Math.round((i / totalPages) * 100)

    onProgress?.({
      stage: 'extracting',
      currentPage: i,
      totalPages,
      percentage,
      message: `Extracting text from page ${i} of ${totalPages}...`,
    })

    const page = await pdf.getPage(i)
    const textContent = await page.getTextContent()

    // Preserve structure by detecting line breaks based on Y position changes
    let lastY = -1
    const lines: string[] = []
    let currentLine = ''

    for (const item of textContent.items) {
      if (!('str' in item)) continue

      // Sanitize text to remove NULL bytes and control characters
      const cleanStr = sanitizeText(item.str)

      // Check if Y position changed significantly (new line)
      const y = (item.transform as number[] | undefined)?.[5] || 0
      if (lastY !== -1 && Math.abs(y - lastY) > 5) {
        // New line detected
        if (currentLine.trim()) {
          lines.push(currentLine.trim())
        }
        currentLine = cleanStr
      } else {
        // Same line - add space if needed
        if (currentLine && !currentLine.endsWith(' ') && !cleanStr.startsWith(' ')) {
          currentLine += ' '
        }
        currentLine += cleanStr
      }
      lastY = y
    }

    // Don't forget the last line
    if (currentLine.trim()) {
      lines.push(currentLine.trim())
    }

    // Join lines with newlines to preserve structure
    const text = lines.join('\n')

    pages.push({
      pageNumber: i,
      text: text.trim(),
    })
  }

  onProgress?.({
    stage: 'complete',
    currentPage: totalPages,
    totalPages,
    percentage: 100,
    message: `Extracted ${totalPages} pages`,
  })

  // Build fullText and apply final sanitization pass
  const fullText = pages.map((p) => `--- Page ${p.pageNumber} ---\n${p.text}`).join('\n\n')

  return {
    fileName: file.name,
    totalPages,
    pages,
    fullText: sanitizeText(fullText), // Final sanitization pass to ensure no NULL bytes
  }
}

/**
 * Get PDF info without full extraction (for progress estimation)
 */
export async function getPdfInfo(file: File): Promise<{ totalPages: number; sizeKB: number }> {
  const arrayBuffer = await file.arrayBuffer()
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
  return {
    totalPages: pdf.numPages,
    sizeKB: Math.round(file.size / 1024),
  }
}
